import type { Witness } from '../store/booksStore'
import { getRowLabel, type AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import {
  applyLedgerSafeClearGap,
  applyLedgerSafeInsertRowAndMove,
  applyLedgerSafeMarkGap,
  applyLedgerSafeMoveMapping,
  applyLedgerSafeShiftRange
} from './ledgerSafeRowEditing'
import { normalizeLedgerSafeSuggestionRowIds } from './ledgerSafeEditSuggestionEvents'
import {
  asLedgerSafeSuggestionString,
  getLedgerSafeEventRowText,
  getLedgerSafeSuggestionWitnessLabel,
  getLedgerSafeWitnessRowText,
  getLedgerSafeWitnessWindowText
} from './ledgerSafeEditSuggestionRows'
import {
  describeLedgerSafeSuggestionContentMatch,
  MIN_LEDGER_SAFE_SINGLE_ROW_CONTENT_SCORE,
  MIN_LEDGER_SAFE_WINDOW_CONTENT_SCORE,
  scoreLedgerSafeSuggestionTextSimilarity
} from './ledgerSafeEditSuggestionScoring'
import type { LedgerSafeEditSuggestion } from './ledgerSafeEditSuggestionTypes'
import type { LedgerSafeRowMotionSuggestionKind } from './ledgerSafeEditSuggestionRouting'

export const scoreLedgerSafeRowMotionSuggestionContent = ({
  event,
  rows,
  rowIds,
  kind,
  sourceWitnessId,
  targetWitnessId,
  sourceSlotRowId
}: {
  event: LedgerSafeEditorialEvent
  rows: AnchoredRow[]
  rowIds: string[]
  kind: LedgerSafeRowMotionSuggestionKind
  sourceWitnessId: string
  targetWitnessId: string
  sourceSlotRowId: string
}): number => {
  if (kind === 'mark_gap' || kind === 'clear_gap') {
    return 0.55
  }
  const sourceReferenceText = sourceWitnessId
    ? (
      (sourceSlotRowId ? getLedgerSafeWitnessRowText(rows, sourceSlotRowId, sourceWitnessId) : '') ||
      rowIds
        .map(rowId => getLedgerSafeEventRowText(event, 'beforeRows', sourceWitnessId, rowId) || getLedgerSafeWitnessRowText(rows, rowId, sourceWitnessId))
        .filter(Boolean)
        .join(' ')
    )
    : ''
  const targetReferenceText = getLedgerSafeWitnessWindowText(rows, rowIds, targetWitnessId)
  return scoreLedgerSafeSuggestionTextSimilarity(sourceReferenceText, targetReferenceText)
}

export const rowMotionLedgerSafeSuggestionPassesContentGate = ({
  kind,
  rowIds,
  contentScore
}: {
  kind: LedgerSafeRowMotionSuggestionKind
  rowIds: string[]
  contentScore: number
}): boolean => {
  if (kind === 'mark_gap' || kind === 'clear_gap') {
    return true
  }
  const contentThreshold = rowIds.length > 1
    ? MIN_LEDGER_SAFE_WINDOW_CONTENT_SCORE
    : MIN_LEDGER_SAFE_SINGLE_ROW_CONTENT_SCORE
  return contentScore >= contentThreshold
}

export const buildLedgerSafeRowMotionSuggestionLabel = ({
  kind,
  rowIds,
  params
}: {
  kind: LedgerSafeRowMotionSuggestionKind
  rowIds: string[]
  params: Record<string, unknown>
}): string => {
  if (kind === 'move_mapping') {
    return `Move ${params.direction === 'up' ? 'up' : 'down'}`
  }
  if (kind === 'shift_range') {
    return `Shift ${rowIds.length} rows ${params.direction === 'up' ? 'up' : 'down'}`
  }
  if (kind === 'insert_row_and_move') {
    return `Insert ${params.placement === 'above' ? 'above' : 'below'}`
  }
  return kind === 'mark_gap' ? 'Mark gap' : 'Clear gap'
}

export const buildLedgerSafeRowMotionSuggestionDetail = ({
  targetLabel,
  rows,
  rowIds
}: {
  targetLabel: string
  rows: AnchoredRow[]
  rowIds: string[]
}): string => {
  if (rowIds.length !== 1) {
    return `${targetLabel} · ${rowIds.length} rows`
  }
  const row = rows.find(candidate => candidate.id === rowIds[0])
  return `${targetLabel} · ${row ? getRowLabel(row) : rowIds[0]}`
}

export const buildLedgerSafeRowMotionSuggestion = ({
  event,
  rows,
  targetWitness,
  sourceWitness,
  kind,
  rowIds,
  params
}: {
  event: LedgerSafeEditorialEvent
  rows: AnchoredRow[]
  targetWitness: Witness
  sourceWitness: Witness | undefined
  kind: LedgerSafeRowMotionSuggestionKind
  rowIds: string[]
  params: Record<string, unknown>
}): LedgerSafeEditSuggestion | null => {
  try {
    if (kind === 'move_mapping') {
      applyLedgerSafeMoveMapping({
        rows,
        witnessId: targetWitness.id,
        rowId: asLedgerSafeSuggestionString(params.rowId),
        direction: params.direction === 'up' ? 'up' : 'down'
      })
    } else if (kind === 'insert_row_and_move') {
      applyLedgerSafeInsertRowAndMove({
        rows,
        witnessId: targetWitness.id,
        rowId: asLedgerSafeSuggestionString(params.rowId),
        placement: params.placement === 'above' ? 'above' : 'below'
      })
    } else if (kind === 'shift_range') {
      applyLedgerSafeShiftRange({
        rows,
        witnessId: targetWitness.id,
        rowIds,
        direction: params.direction === 'up' ? 'up' : 'down'
      })
    } else if (kind === 'mark_gap') {
      applyLedgerSafeMarkGap({
        rows,
        witnessId: targetWitness.id,
        rowId: asLedgerSafeSuggestionString(params.rowId)
      })
    } else {
      applyLedgerSafeClearGap({
        rows,
        witnessId: targetWitness.id,
        rowId: asLedgerSafeSuggestionString(params.rowId)
      })
    }
  } catch {
    return null
  }

  const sourceWitnessId = sourceWitness?.id || ''
  const sourceSlotRowId = asLedgerSafeSuggestionString(params.sourceSlotRowId)
  const contentScore = scoreLedgerSafeRowMotionSuggestionContent({
    event,
    rows,
    rowIds,
    kind,
    sourceWitnessId,
    targetWitnessId: targetWitness.id,
    sourceSlotRowId
  })
  if (!rowMotionLedgerSafeSuggestionPassesContentGate({ kind, rowIds, contentScore })) {
    return null
  }

  const sourceLabel = getLedgerSafeSuggestionWitnessLabel(sourceWitness, 'another witness')
  const targetLabel = getLedgerSafeSuggestionWitnessLabel(targetWitness, targetWitness.id)
  const label = buildLedgerSafeRowMotionSuggestionLabel({ kind, rowIds, params })

  return {
    id: `${event.id}:${kind}:${targetWitness.id}:${normalizeLedgerSafeSuggestionRowIds(rowIds)}:${asLedgerSafeSuggestionString(params.direction || params.placement)}`,
    kind,
    sourceEventId: event.id,
    sourceWitnessId: sourceWitness?.id || '',
    targetWitnessId: targetWitness.id,
    rowIds,
    primaryRowId: rowIds[0],
    params: {
      ...params,
      witnessId: targetWitness.id
    },
    label,
    detail: buildLedgerSafeRowMotionSuggestionDetail({ targetLabel, rows, rowIds }),
    reason: `Suggested because you made this row operation in ${sourceLabel}. ${kind === 'mark_gap' || kind === 'clear_gap' ? 'The same gap state is currently editable.' : describeLedgerSafeSuggestionContentMatch(contentScore)}`,
    confidence: kind === 'mark_gap' || kind === 'clear_gap' || kind === 'insert_row_and_move' ? 'medium' : 'high',
    contentScore
  }
}
